import * as THREE from 'three';
import { ASTRONOMICAL_CONSTANTS, calculateProportionalDistance } from './solarSystemData.js';

/**
 * SolarSystemStructures - Manages the visualization of outer solar system structures
 * Handles asteroid belt, Kuiper belt, and Oort cloud particle systems
 */
export class SolarSystemStructures {
    constructor(scene, initialSunPosition = null) {
        this.scene = scene;

        // Structure visibility states
        this.asteroidBeltVisible = false;
        this.kuiperBeltVisible = false;
        this.oortCloudVisible = false;
        this.heliosphereVisible = false;

        // Particle systems and meshes
        this.asteroidBelt = null;
        this.kuiperBelt = null;
        this.oortCloud = null;
        this.heliosphere = null; // grupo con choque de terminación + heliopausa

        // Materials
        this.asteroidMaterial = null;
        this.kuiperMaterial = null;
        this.oortMaterial = null;
        this.heliopauseMaterial = null;
        this.terminationShockMaterial = null;

        // Sun position tracking
        this.sunPosition = initialSunPosition
            ? initialSunPosition.clone()
            : new THREE.Vector3(0, 0, 0);

        this.initializeMaterials();
    }

    /**
     * Initialize materials for different structure types
     */
    initializeMaterials() {
        // Asteroid belt material - smaller, more numerous particles
        this.asteroidMaterial = new THREE.PointsMaterial({
            color: 0xcccccc,
            size: 1.0,
            transparent: true,
            opacity: 0.2,
            sizeAttenuation: true,
            vertexColors: true,
        });

        // Kuiper belt material - slightly larger, icy appearance
        this.kuiperMaterial = new THREE.PointsMaterial({
            color: 0xaaccff,
            size: 3.0,
            transparent: true,
            opacity: 0.6,
            sizeAttenuation: true,
            vertexColors: true,
        });

        // Oort cloud material - semitransparent sphere
        this.oortMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.05,
            side: THREE.DoubleSide,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });

        // Heliopausa: borde de la heliosfera (azul tenue)
        this.heliopauseMaterial = new THREE.MeshBasicMaterial({
            color: 0x3a6ea5,
            transparent: true,
            opacity: 0.09,
            side: THREE.DoubleSide,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });

        // Choque de terminación: esfera interior, más tenue y cálida
        this.terminationShockMaterial = new THREE.MeshBasicMaterial({
            color: 0xffaa55,
            transparent: true,
            opacity: 0.06,
            side: THREE.DoubleSide,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });
    }

    /**
     * Create asteroid belt particle system
     */
    createAsteroidBelt() {
        if (this.asteroidBelt) return;

        const config = ASTRONOMICAL_CONSTANTS.ASTEROID_BELT;
        const geometry = new THREE.BufferGeometry();
        const positions = [];
        const colors = [];

        console.log(
            'Creating asteroid belt with sun position:',
            this.sunPosition.x,
            this.sunPosition.y,
            this.sunPosition.z
        );

        for (let i = 0; i < config.particleCount; i++) {
            // Random position in asteroid belt region - in XY plane like planetary orbits
            const angle = Math.random() * Math.PI * 2;
            const radiusAU =
                config.innerRadius + Math.random() * (config.outerRadius - config.innerRadius);
            const heightAU = (Math.random() - 0.5) * config.thickness;

            // Convert AU to scaled world units
            const radius = calculateProportionalDistance(radiusAU * ASTRONOMICAL_CONSTANTS.AU);
            const height = calculateProportionalDistance(heightAU * ASTRONOMICAL_CONSTANTS.AU);

            // Create in XY plane (same as planetary orbits), with Z as height variation
            // These are LOCAL coordinates relative to the belt center
            positions.push(
                Math.cos(angle) * radius, // X (local)
                Math.sin(angle) * radius, // Y (local)
                height // Z (local thickness variation)
            );

            // Vary colors between white and gray
            const brightness = 0.5 + Math.random() * 0.5;
            colors.push(brightness, brightness, brightness);
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        this.asteroidBelt = new THREE.Points(geometry, this.asteroidMaterial);
        this.asteroidBelt.name = 'asteroidBelt';

        // Position the entire belt at the sun's location
        this.asteroidBelt.position.copy(this.sunPosition);

        console.log(
            'Asteroid belt positioned at:',
            this.asteroidBelt.position.x,
            this.asteroidBelt.position.y,
            this.asteroidBelt.position.z
        );

        // Apply inclination relative to ecliptic plane
        const inclinationRad = (config.inclination * Math.PI) / 180;
        this.asteroidBelt.rotation.x = inclinationRad;

        this.scene.add(this.asteroidBelt);
    }

    /**
     * Create Kuiper belt particle system
     */
    createKuiperBelt() {
        if (this.kuiperBelt) return;

        const config = ASTRONOMICAL_CONSTANTS.KUIPER_BELT;
        const geometry = new THREE.BufferGeometry();
        const positions = [];
        const colors = [];

        for (let i = 0; i < config.particleCount; i++) {
            // Random position in Kuiper belt region - in XY plane like planetary orbits
            const angle = Math.random() * Math.PI * 2;
            const radiusAU =
                config.innerRadius + Math.random() * (config.outerRadius - config.innerRadius);
            const heightAU = (Math.random() - 0.5) * config.thickness;

            // Convert AU to scaled world units
            const radius = calculateProportionalDistance(radiusAU * ASTRONOMICAL_CONSTANTS.AU);
            const height = calculateProportionalDistance(heightAU * ASTRONOMICAL_CONSTANTS.AU);

            // Create in XY plane (same as planetary orbits), with Z as height variation
            positions.push(
                Math.cos(angle) * radius, // X
                Math.sin(angle) * radius, // Y
                height // Z (thickness variation)
            );

            // Icy blue-white colors
            const brightness = 0.6 + Math.random() * 0.4;
            colors.push(brightness * 0.8, brightness * 0.9, brightness);
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        this.kuiperBelt = new THREE.Points(geometry, this.kuiperMaterial);
        this.kuiperBelt.name = 'kuiperBelt';
        this.kuiperBelt.position.copy(this.sunPosition);

        // Apply inclination relative to ecliptic plane
        const inclinationRad = (config.inclination * Math.PI) / 180;
        this.kuiperBelt.rotation.x = inclinationRad;

        this.scene.add(this.kuiperBelt);
    }

    /**
     * Create Oort cloud as semitransparent sphere
     */
    createOortCloud() {
        if (this.oortCloud) return;

        const config = ASTRONOMICAL_CONSTANTS.OORT_CLOUD;

        // Use average radius for the sphere
        const averageRadiusAU = (config.innerRadius + config.outerRadius) / 2;
        const radius = calculateProportionalDistance(averageRadiusAU * ASTRONOMICAL_CONSTANTS.AU);

        // Create sphere geometry
        const geometry = new THREE.SphereGeometry(radius, 32, 16);

        this.oortCloud = new THREE.Mesh(geometry, this.oortMaterial);
        this.oortCloud.name = 'oortCloud';
        this.oortCloud.position.copy(this.sunPosition);
        this.scene.add(this.oortCloud);
    }

    /**
     * Crea la heliosfera como dos esferas translúcidas concéntricas (choque de terminación y
     * heliopausa), centradas en el Sol. Distancias de las Voyager (config HELIOSPHERE).
     */
    createHeliosphere() {
        if (this.heliosphere) return;

        const config = ASTRONOMICAL_CONSTANTS.HELIOSPHERE;
        const shockRadius = calculateProportionalDistance(
            config.terminationShock * ASTRONOMICAL_CONSTANTS.AU
        );
        const pauseRadius = calculateProportionalDistance(
            config.heliopause * ASTRONOMICAL_CONSTANTS.AU
        );

        this.heliosphere = new THREE.Group();
        this.heliosphere.name = 'heliosphere';

        // Forma de "gota"/cometa: morro hacia +Z (avance del Sol) y cola (heliotail) detrás.
        const shock = new THREE.Mesh(
            this.createTeardropGeometry(shockRadius),
            this.terminationShockMaterial
        );
        shock.name = 'terminationShock';

        const pause = new THREE.Mesh(
            this.createTeardropGeometry(pauseRadius),
            this.heliopauseMaterial
        );
        pause.name = 'heliopause';

        this.heliosphere.add(shock);
        this.heliosphere.add(pause);
        this.heliosphere.position.copy(this.sunPosition);
        this.scene.add(this.heliosphere);
    }

    /**
     * Geometría tipo "gota"/cometa de la heliosfera (forma hipotética): morro redondeado y una
     * larga cola (heliotail). Se genera por revolución (LatheGeometry) de un perfil y se orienta
     * con el morro hacia +Z, que es la dirección de avance del Sol en el simulador (el viento
     * interestelar comprime el lado de avance y estira el opuesto). La longitud de la cola es
     * muy incertidumbre; se usa ~3,5x el radio del morro como representación.
     * @param {number} R - Radio del morro/heliopausa (≈ radio máximo del cuerpo)
     * @returns {THREE.BufferGeometry}
     */
    createTeardropGeometry(R) {
        const tail = R * 3.5;
        const profile = [
            new THREE.Vector2(0.001, R), // morro (punta frontal)
            new THREE.Vector2(R * 0.5, R * 0.82),
            new THREE.Vector2(R * 0.85, R * 0.5),
            new THREE.Vector2(R * 0.99, R * 0.15),
            new THREE.Vector2(R, -R * 0.25), // zona más ancha (cuerpo)
            new THREE.Vector2(R * 0.95, -R * 0.7),
            new THREE.Vector2(R * 0.78, -R * 1.3),
            new THREE.Vector2(R * 0.55, -R * 2.1),
            new THREE.Vector2(R * 0.33, -R * 3.0),
            new THREE.Vector2(R * 0.16, -tail * 0.97),
            new THREE.Vector2(0.001, -tail), // punta de la cola
        ];
        const geom = new THREE.LatheGeometry(profile, 48);
        geom.rotateX(Math.PI / 2); // eje de revolución (Y) -> +Z: el morro apunta al avance del Sol
        return geom;
    }

    /**
     * Show asteroid belt
     */
    showAsteroidBelt() {
        if (!this.asteroidBelt) {
            this.createAsteroidBelt();
            // Ensure it's positioned correctly after creation
            this.asteroidBelt.position.copy(this.sunPosition);
        }
        this.asteroidBelt.visible = true;
        this.asteroidBeltVisible = true;
        console.log('Asteroid belt shown');
    }

    /**
     * Hide asteroid belt
     */
    hideAsteroidBelt() {
        if (this.asteroidBelt) {
            this.asteroidBelt.visible = false;
        }
        this.asteroidBeltVisible = false;
        console.log('Asteroid belt hidden');
    }

    /**
     * Show Kuiper belt
     */
    showKuiperBelt() {
        if (!this.kuiperBelt) {
            this.createKuiperBelt();
            // Ensure it's positioned correctly after creation
            this.kuiperBelt.position.copy(this.sunPosition);
        }
        this.kuiperBelt.visible = true;
        this.kuiperBeltVisible = true;
        console.log('Kuiper belt shown');
    }

    /**
     * Hide Kuiper belt
     */
    hideKuiperBelt() {
        if (this.kuiperBelt) {
            this.kuiperBelt.visible = false;
        }
        this.kuiperBeltVisible = false;
        console.log('Kuiper belt hidden');
    }

    /**
     * Show Oort cloud
     */
    showOortCloud() {
        if (!this.oortCloud) {
            this.createOortCloud();
            // Ensure it's positioned correctly after creation
            this.oortCloud.position.copy(this.sunPosition);
        }
        this.oortCloud.visible = true;
        this.oortCloudVisible = true;
        console.log('Oort cloud shown');
    }

    /**
     * Hide Oort cloud
     */
    hideOortCloud() {
        if (this.oortCloud) {
            this.oortCloud.visible = false;
        }
        this.oortCloudVisible = false;
        console.log('Oort cloud hidden');
    }

    /**
     * Show heliosphere
     */
    showHeliosphere() {
        if (!this.heliosphere) {
            this.createHeliosphere();
            this.heliosphere.position.copy(this.sunPosition);
        }
        this.heliosphere.visible = true;
        this.heliosphereVisible = true;
        console.log('Heliosphere shown');
    }

    /**
     * Hide heliosphere
     */
    hideHeliosphere() {
        if (this.heliosphere) {
            this.heliosphere.visible = false;
        }
        this.heliosphereVisible = false;
        console.log('Heliosphere hidden');
    }

    /**
     * Update sun position and move structures accordingly
     * @param {THREE.Vector3} sunPosition - Current sun position
     */
    updateSunPosition(sunPosition) {
        // console.log('Updating structures sun position to:', sunPosition.x, sunPosition.y, sunPosition.z);
        this.sunPosition.copy(sunPosition);

        // Update positions of all visible structures (rotation is preserved)
        if (this.asteroidBelt && this.asteroidBeltVisible) {
            this.asteroidBelt.position.copy(this.sunPosition);
            console.log(
                'Asteroid belt positioned at:',
                this.asteroidBelt.position.x,
                this.asteroidBelt.position.y,
                this.asteroidBelt.position.z
            );
        }

        if (this.kuiperBelt && this.kuiperBeltVisible) {
            this.kuiperBelt.position.copy(this.sunPosition);
            console.log(
                'Kuiper belt positioned at:',
                this.kuiperBelt.position.x,
                this.kuiperBelt.position.y,
                this.kuiperBelt.position.z
            );
        }

        if (this.oortCloud && this.oortCloudVisible) {
            this.oortCloud.position.copy(this.sunPosition);
            console.log(
                'Oort cloud positioned at:',
                this.oortCloud.position.x,
                this.oortCloud.position.y,
                this.oortCloud.position.z
            );
        }

        if (this.heliosphere && this.heliosphereVisible) {
            this.heliosphere.position.copy(this.sunPosition);
        }
    }

    /**
     * Update structures (called in animation loop)
     * @param {number} deltaTime - Time since last frame
     */
    update(deltaTime) {
        // Structures are static relative to the solar system
        // No animation needed unless we want subtle effects
    }

    /**
     * Hace orbitar los cinturones alrededor del Sol según el TIEMPO DE SIMULACIÓN, de modo que
     * su velocidad escala con la escala de tiempo igual que los planetas. La rotación es absoluta
     * (no acumulada) → determinista y sin deriva. El cinturón interior orbita más rápido que el de
     * Kuiper (cualitativamente, 3ª ley de Kepler). La nube de Oort es esférica: rotarla no aporta
     * nada visual, así que se omite.
     *
     * IMPORTANTE: las partículas se distribuyen en el plano XY (igual que las órbitas
     * planetarias), por lo que el eje orbital es Z, no Y. Se rota en rotation.z (con la
     * inclinación ya aplicada en rotation.x, el orden Euler XYZ hace que Z sea la normal del
     * disco) y en sentido positivo para coincidir con el giro antihorario de los planetas.
     * @param {number} currentTimeDays - Tiempo de simulación en días (SimulationEngine.currentTime)
     */
    updateRotation(currentTimeDays) {
        if (typeof currentTimeDays !== 'number') return;
        const TWO_PI = Math.PI * 2;
        const ASTEROID_PERIOD = 1680; // ~4.6 años (cinturón principal de asteroides)
        const KUIPER_PERIOD = 90000; // ~246 años (objetos transneptunianos)

        if (this.asteroidBelt) {
            this.asteroidBelt.rotation.z = (currentTimeDays / ASTEROID_PERIOD) * TWO_PI;
        }
        if (this.kuiperBelt) {
            this.kuiperBelt.rotation.z = (currentTimeDays / KUIPER_PERIOD) * TWO_PI;
        }
    }

    /**
     * Get visibility states
     */
    getVisibilityStates() {
        return {
            asteroidBelt: this.asteroidBeltVisible,
            kuiperBelt: this.kuiperBeltVisible,
            oortCloud: this.oortCloudVisible,
            heliosphere: this.heliosphereVisible,
        };
    }

    /**
     * Dispose of all resources
     */
    dispose() {
        // Remove from scene and dispose geometries/materials
        if (this.asteroidBelt) {
            this.scene.remove(this.asteroidBelt);
            this.asteroidBelt.geometry.dispose();
        }

        if (this.kuiperBelt) {
            this.scene.remove(this.kuiperBelt);
            this.kuiperBelt.geometry.dispose();
        }

        if (this.oortCloud) {
            this.scene.remove(this.oortCloud);
            this.oortCloud.geometry.dispose();
        }

        if (this.heliosphere) {
            this.scene.remove(this.heliosphere);
            this.heliosphere.children.forEach((mesh) => mesh.geometry.dispose());
        }

        // Dispose materials
        this.asteroidMaterial?.dispose();
        this.kuiperMaterial?.dispose();
        this.oortMaterial?.dispose();
        this.heliopauseMaterial?.dispose();
        this.terminationShockMaterial?.dispose();

        console.log('Solar system structures disposed');
    }
}
